import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { IJobFetch, QuestionCreation } from "../../store/types/job";
import { formatDate } from "../../utility/formatDate";
import { getAssessmentById, getPretestById } from "../../services/api/job";

interface JobDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  job: IJobFetch | null;
}

const JobDetailsModal: React.FC<JobDetailsModalProps> = ({
  isOpen,
  onClose,
  job,
}) => {
  const [pretestData, setPretestData] = useState<QuestionCreation | null>(null);
  const [assessmentData, setAssessmentData] = useState<QuestionCreation | null>(
    null,
  );

  useEffect(() => {
    const fetchData = async () => {
      if (job?.currentTemplate?.pretestId) {
        try {
          const data = await getPretestById(job.currentTemplate.pretestId);
          setPretestData(data);
        } catch (error) {
          console.error("Error fetching pretest:", error);
        }
      } else {
        setPretestData(null);
      }

      if (job?.currentTemplate?.assessmentId) {
        try {
          const data = await getAssessmentById(
            job.currentTemplate.assessmentId,
          );
          setAssessmentData(data);
        } catch (error) {
          console.error("Error fetching assessment:", error);
        }
      } else {
        setAssessmentData(null);
      }
    };

    if (isOpen && job) {
      fetchData();
    }
  }, [isOpen, job]);

  if (!isOpen || !job) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-900">Job Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Job Title */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">
              Job Title
            </h3>
            <p className="text-lg font-medium text-gray-900">
              {job.currentTemplate.title}
            </p>
          </div>

          {/* Description */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">
              Description
            </h3>
            <p className="text-gray-700 whitespace-pre-wrap">
              {job.currentTemplate.description}
            </p>
          </div>

          {/* Interview Mode */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">
                Interview Mode
              </h3>
              <p className="text-gray-900 capitalize">
                {job.currentTemplate.interviewMode
                  ?.replace(/_+/g, " ")
                  .toLowerCase()}
              </p>
            </div>

            {/* Date Created */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">
                Date Created
              </h3>
              <p className="text-gray-900">
                {formatDate(job?.currentTemplate?.createdAt)}
              </p>
            </div>
          </div>

          {/* Pretest/Assessment Status */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">
              Pretest/Assessment
            </h3>
            <div className="flex items-center gap-2">
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  job.currentTemplate.pretestId
                    ? "bg-green-100 text-green-700"
                    : "bg-gray-100 text-gray-700"
                }`}
              >
                {job.currentTemplate.pretestId ? "Active" : "None"}
              </span>
            </div>
          </div>

          {/* Questions */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-2">
              Interview Questions
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
              <ul className="space-y-3">
                {(() => {
                  const questions =
                    job.currentTemplate.interviewQuestions?.length > 0
                      ? job.currentTemplate.interviewQuestions
                      : job.currentTemplate.questions?.map(
                          (q: any) => q.question || q,
                        ) || [];

                  return questions.length > 0 ? (
                    questions.map((question, index) => (
                      <li key={index} className="flex gap-3 text-gray-700">
                        <span className="font-medium text-gray-400 min-w-[20px]">
                          {index + 1}.
                        </span>
                        <span>{question}</span>
                      </li>
                    ))
                  ) : (
                    <li className="text-gray-500 italic">
                      No questions available
                    </li>
                  );
                })()}
              </ul>
            </div>
          </div>

          {/* Pretest Details */}
          {pretestData && (
            <div className="border-t border-gray-100 pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Pretest Details
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Title</h4>
                    <p className="text-gray-900">{pretestData.jobTitle}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">
                      Duration
                    </h4>
                    <p className="text-gray-900">{pretestData.duration} mins</p>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">
                    Questions
                  </h4>
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                    <ul className="space-y-3">
                      {pretestData.questions.map((question, index) => (
                        <li key={index} className="flex gap-3 text-gray-700">
                          <span className="font-medium text-blue-400 min-w-[20px]">
                            {index + 1}.
                          </span>
                          <span>{question}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Assessment Details */}
          {assessmentData && (
            <div className="border-t border-gray-100 pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Assessment Details
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Title</h4>
                    <p className="text-gray-900">{assessmentData.jobTitle}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">
                      Duration
                    </h4>
                    <p className="text-gray-900">
                      {assessmentData.duration} mins
                    </p>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">
                    Questions
                  </h4>
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                    <ul className="space-y-3">
                      {assessmentData.questions.map((question, index) => (
                        <li key={index} className="flex gap-3 text-gray-700">
                          <span className="font-medium text-purple-400 min-w-[20px]">
                            {index + 1}.
                          </span>
                          <span>{question}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 bg-gray-50 rounded-b-xl flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobDetailsModal;
